import { useState } from "react";
import { toast } from "sonner";
import { Check, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CURRENCIES, currencyMeta, applyTenantCurrency } from "@/lib/currency";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Compact currency picker for the top header — one click on any page.
 * Owners/managers/super-admins can change it here; other roles see a
 * read-only badge (no click).
 */
export function CurrencySwitcher() {
  const { user, tenant, setTenant } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const current = currencyMeta(tenant?.currency);
  const canEdit = user && ["business_owner", "manager", "super_admin"].includes(user.role);

  const pick = async (code) => {
    if (code === current.code) { setOpen(false); return; }
    setBusy(true);
    // Optimistic — apply locally so every formatMoney() reflects immediately.
    applyTenantCurrency(code);
    try {
      const { data } = await api.patch("/tenant/branding", { currency: code });
      setTenant(data);
      toast.success(`Currency set to ${code}`);
    } catch (e) {
      applyTenantCurrency(tenant?.currency || "USD");
      toast.error(e?.response?.data?.detail || "Could not update currency.");
    }
    setBusy(false);
    setOpen(false);
  };

  const trigger = (
    <button
      data-testid="header-currency-btn"
      title={canEdit ? `Currency · ${current.label} (${current.code})` : `${current.label}`}
      aria-label={`Currency: ${current.label}`}
      disabled={busy || !canEdit}
      className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-60"
    >
      <span className="text-base leading-none">{current.flag}</span>
      <span className="font-mono uppercase tracking-wider text-[11px]">{current.code}</span>
      {canEdit && <ChevronDown className="h-3 w-3 opacity-60" />}
    </button>
  );

  if (!canEdit) return trigger;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0 max-h-80 overflow-y-auto" data-testid="header-currency-popover">
        <div className="p-1">
          {CURRENCIES.map((c) => (
            <button
              key={c.code}
              onClick={() => pick(c.code)}
              data-testid={`header-currency-item-${c.code}`}
              className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted transition-colors text-left"
            >
              <span className="text-base leading-none">{c.flag}</span>
              <span className="font-mono w-10 text-[11px] uppercase text-muted-foreground">{c.code}</span>
              <span className="flex-1 truncate">{c.label}</span>
              <span className="text-muted-foreground text-xs">{c.symbol}</span>
              {c.code === current.code && <Check className="h-3.5 w-3.5 text-primary" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
